import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Wallet, ArrowUpRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { getAgentDashboard, getCommissionRecords } from '../../lib/api';

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
};

const StatCard = ({ title, value, icon: Icon, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  };

  return (
    <Card className="border border-border hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className="text-2xl font-bold mt-1 font-['Plus_Jakarta_Sans']">{value}</p>
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AgentDashboard() {
  const location = useLocation();
  const { token } = useAuth();
  const [commissionStats, setCommissionStats] = useState({
    commissionEarned: 0,
    pendingCommission: 0,
    paidCommission: 0,
  });
  const [commissionChart, setCommissionChart] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const section = (() => {
    if (location.pathname.includes('/agent/dashboard/leads')) return 'leads';
    if (location.pathname.includes('/agent/dashboard/earnings')) return 'earnings';
    if (location.pathname.includes('/agent/dashboard/performance')) return 'performance';
    return 'overview';
  })();

  useEffect(() => {
    let active = true;
    async function load() {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const [dashboardResp, recordsResp] = await Promise.all([
          getAgentDashboard(token),
          getCommissionRecords(token),
        ]);
        if (!active) return;
        const data = dashboardResp?.data || {};
        const items = Array.isArray(recordsResp?.data?.items) ? recordsResp.data.items : [];
        setCommissionStats({
          commissionEarned: Number(data.commissionEarned || 0),
          pendingCommission: Number(data.pendingCommission || 0),
          paidCommission: Number(data.paidCommission || 0),
        });
        setCommissionChart(
          Array.isArray(data.commissionOverview) && data.commissionOverview.length
            ? data.commissionOverview
            : [],
        );
        setRecords(items);
      } catch (err) {
        if (!active) return;
        setCommissionStats({
          commissionEarned: 0,
          pendingCommission: 0,
          paidCommission: 0,
        });
        setCommissionChart([]);
        setRecords([]);
        setError(err?.message || 'Failed to load commission data');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const approvedCommission = useMemo(
    () =>
      records
        .filter((item) => item.status === 'approved')
        .reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [records],
  );

  const recentRecords = useMemo(() => records.slice(0, 5), [records]);

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const badgeClass = (status) => (
    status === 'paid'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'approved'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-amber-50 text-amber-700 border-amber-200'
  );

  return (
    <div className="space-y-6" data-testid="agent-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Agent Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            {section === 'overview'
              ? "Your leads, files and earnings at a glance."
              : section === 'leads'
                ? 'Lead summary and pipeline.'
                : section === 'earnings'
                  ? 'Earnings and commission snapshot.'
                  : 'Performance tracker.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/agent/payouts/status">Payout Status</Link>
          </Button>
          <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
            <Link to="/agent/leads/add">Add New Lead</Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Commission Earned" value={formatCurrency(commissionStats.commissionEarned)} icon={Wallet} color="blue" />
        <StatCard title="Pending Commission" value={formatCurrency(commissionStats.pendingCommission)} icon={Wallet} color="amber" />
        <StatCard title="Paid Commission" value={formatCurrency(commissionStats.paidCommission)} icon={Wallet} color="emerald" />
        <StatCard title="Approved Commission" value={formatCurrency(approvedCommission)} icon={Wallet} color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border border-border lg:col-span-2">
          <CardHeader>
            <CardTitle>Earnings Summary</CardTitle>
            <CardDescription>Paid vs pending commissions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={commissionChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(v) => `₹${v / 1000}K`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, '']}
                  />
                  <Bar dataKey="paid" fill="#10B981" name="Paid" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pending" fill="#F59E0B" name="Pending" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {!commissionChart.length && !loading ? (
              <p className="text-sm text-muted-foreground">No commission trend available yet.</p>
            ) : null}
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Commission Actions</CardTitle>
            <CardDescription>Open the role-based commission and payout screens.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button asChild variant="outline" size="sm" className="w-full">
              <Link to="/agent/earnings/history">
                Commission History <ArrowUpRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="w-full">
              <Link to="/agent/notifications">
                Notifications <ArrowUpRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="w-full">
              <Link to="/agent/payouts/history">
                Payout History <ArrowUpRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Recent Commission Entries</CardTitle>
          <CardDescription>Your latest payout-relevant commission records.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Loan</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    Loading commission entries...
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && error ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    {error}
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && !error && recentRecords.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-sm">{row.loan_application_id}</TableCell>
                  <TableCell>{row.bank_name || '—'}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(Number(row.amount || 0))}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={badgeClass(row.status)}>
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(row.paid_at || row.created_at)}</TableCell>
                </TableRow>
              ))}
              {!loading && !error && recentRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    No commission entries found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
